import { useState, useRef } from 'react'
import { X, ChevronLeft, MessageCircle, AlertTriangle, Check } from 'lucide-react'
import { supabase } from '../../../lib/supabase'

// Mirrors the Aso Ebi bulk-reminder flow: a "Remind all" bar + a two-phase modal
// (compose a templated message, then open WhatsApp for each pending guest one by one).

const RSVP_TEMPLATE = `Hi [First Name] 👋

We're still hoping to celebrate our wedding with you — but we haven't received your RSVP yet!

It only takes a moment. Tap your personal link to let us know if you can make it:
[RSVP Link]

We'd truly love to have you there. 🧡

— [Couple Names]`

// ── Helpers ───────────────────────────────────────────────────
function normalizePhone(phone) {
  if (!phone) return null
  const clean = phone.replace(/[\s\-().+]/g, '')
  if (phone.startsWith('+234')) return clean
  if (clean.startsWith('234')) return clean
  if (clean.startsWith('0')) return '234' + clean.slice(1)
  return clean
}

function personalize(template, guest, wedding) {
  const firstName = (guest.name || '').split(' ')[0]
  const link = `${window.location.origin}/w/${wedding?.slug || ''}/invite/${guest.invite_token || ''}`
  const weddingDate = wedding?.wedding_date
    ? new Date(wedding.wedding_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : 'soon'
  return template
    .replace(/\[First Name\]/g, firstName)
    .replace(/\[RSVP Link\]/g, link)
    .replace(/\[Wedding Date\]/g, weddingDate)
    .replace(/\[Couple Names\]/g, `${wedding?.partner1_name || ''} & ${wedding?.partner2_name || ''}`)
}

// ── Highlighted textarea (placeholders shaded) ────────────────
function HighlightTextArea({ value, onChange }) {
  const taRef = useRef()
  const bdRef = useRef()
  const syncScroll = () => { if (bdRef.current && taRef.current) bdRef.current.scrollTop = taRef.current.scrollTop }
  const highlighted = value
    .split(/(\[[^\]]+\])/g)
    .map(p => /^\[[^\]]+\]$/.test(p)
      ? `<mark style="background:rgba(179,107,85,0.15);color:transparent;border-radius:3px;padding:0 2px">${p}</mark>`
      : p.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>')
    ).join('')
  const shared = {
    fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.82rem', lineHeight: '1.75',
    padding: '0.75rem', width: '100%', boxSizing: 'border-box',
    border: 'none', outline: 'none', resize: 'none',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word', letterSpacing: 'normal', tabSize: 4,
  }
  return (
    <div style={{ position: 'relative', border: '1.5px solid #E5E0D8', borderRadius: '0.75rem', overflow: 'hidden', background: 'white' }}>
      <div ref={bdRef} aria-hidden="true"
        style={{ ...shared, position: 'absolute', inset: 0, color: 'transparent', pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}
        dangerouslySetInnerHTML={{ __html: highlighted + ' ' }}/>
      <textarea ref={taRef} value={value} onChange={e => onChange(e.target.value)} onScroll={syncScroll} spellCheck={false}
        style={{ ...shared, position: 'relative', zIndex: 1, background: 'transparent', color: '#1A1A1A', caretColor: '#1A1A1A', minHeight: 220, display: 'block', margin: 0 }}/>
    </div>
  )
}

// ── Public component: the bar + (when opened) the modal ───────
export default function GuestRsvpReminder({ pending, wedding }) {
  const [open, setOpen] = useState(false)
  if (!pending || pending.length === 0) return null
  return (
    <>
      <div style={{
        background: 'rgba(196,130,106,0.07)', border: '1px solid rgba(196,130,106,0.2)', borderRadius: '0.875rem',
        padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap',
      }}>
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.78rem', color: '#6B5B52', margin: 0 }}>
          {pending.length} guest{pending.length !== 1 ? 's' : ''} {pending.length !== 1 ? "haven't" : "hasn't"} responded yet
        </p>
        <button onClick={() => setOpen(true)} style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#B36B55', color: 'white', border: 'none', borderRadius: 100,
          padding: '0.55rem 1.125rem', cursor: 'pointer', fontFamily: 'Nunito Sans, sans-serif', fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap',
        }}>
          <MessageCircle size={14}/> Remind All to RSVP ({pending.length})
        </button>
      </div>
      {open && <ReminderModal pending={pending} wedding={wedding} onClose={() => setOpen(false)}/>}
    </>
  )
}

// ── The modal ─────────────────────────────────────────────────
function ReminderModal({ pending, wedding, onClose }) {
  const isMobile = window.innerWidth < 768
  const [phase, setPhase]   = useState('compose') // 'compose' | 'links'
  const [message, setMessage] = useState(RSVP_TEMPLATE)
  const [sentSet, setSentSet] = useState(new Set())

  const withPhone    = pending.filter(g => g.phone)
  const withoutPhone = pending.filter(g => !g.phone)
  const progress = withPhone.length > 0 ? Math.round((sentSet.size / withPhone.length) * 100) : 0

  async function handleOpenWhatsApp(guest) {
    const phone = normalizePhone(guest.phone)
    if (!phone) return
    const text = personalize(message, guest, wedding)
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank')
    setSentSet(prev => new Set([...prev, guest.id]))
    try {
      await supabase.from('guests').update({ whatsapp_sent: true, whatsapp_sent_at: new Date().toISOString() }).eq('id', guest.id)
    } catch (e) { /* best-effort */ }
  }

  function handleClose() {
    if (sentSet.size > 0 && sentSet.size < withPhone.length) {
      if (!window.confirm(`You've reminded ${sentSet.size} of ${withPhone.length} guests. Close anyway?`)) return
    }
    onClose()
  }

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: '1px solid #F3F0EA', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {phase === 'links' && (
            <button onClick={() => setPhase('compose')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9B9B9B', padding: 4, marginRight: 4 }}><ChevronLeft size={18}/></button>
          )}
          <p style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1.1rem', color: '#1A1A1A', margin: 0 }}>
            {phase === 'compose' ? 'Send RSVP Reminder' : `Ready to Send — ${withPhone.length} Message${withPhone.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9B9B9B', padding: 4 }}><X size={18}/></button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
        {phase === 'compose'
          ? <ComposePhase message={message} setMessage={setMessage} withPhone={withPhone} withoutPhone={withoutPhone} wedding={wedding}/>
          : <LinksPhase guests={withPhone} withoutPhone={withoutPhone} sentSet={sentSet} onOpenWhatsApp={handleOpenWhatsApp} progress={progress} isMobile={isMobile}/>}
      </div>

      {/* Footer */}
      <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #F3F0EA', display: 'flex', gap: '0.625rem', justifyContent: 'flex-end', flexShrink: 0 }}>
        {phase === 'compose' ? (
          <>
            <button onClick={handleClose} style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#6B6B6B', background: '#F3F0EA', border: 'none', borderRadius: 100, padding: '0.65rem 1.25rem', cursor: 'pointer' }}>Cancel</button>
            <button onClick={() => setPhase('links')} disabled={withPhone.length === 0} style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.85rem', fontWeight: 700, color: 'white', background: '#B36B55', border: 'none', borderRadius: 100, padding: '0.65rem 1.5rem', cursor: withPhone.length ? 'pointer' : 'default', opacity: withPhone.length ? 1 : 0.5 }}>
              Generate WhatsApp Links →
            </button>
          </>
        ) : (
          <button onClick={onClose} style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.85rem', fontWeight: 700, color: 'white', background: '#1A1A1A', border: 'none', borderRadius: 100, padding: '0.65rem 1.5rem', cursor: 'pointer', width: '100%' }}>Done</button>
        )}
      </div>
    </div>
  )

  if (!isMobile) return (
    <div onClick={handleClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#FEFCFA', borderRadius: '1.25rem', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', width: '100%', maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {content}
      </div>
    </div>
  )

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200 }}>
      <div onClick={handleClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }}/>
      <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#FEFCFA', borderRadius: '1.25rem 1.25rem 0 0', height: '87vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '0.75rem 0 0' }}>
          <div style={{ width: 36, height: 4, background: '#E5E0D8', borderRadius: 100 }}/>
        </div>
        {content}
      </div>
    </div>
  )
}

// ── Compose phase ─────────────────────────────────────────────
function ComposePhase({ message, setMessage, withPhone, withoutPhone, wedding }) {
  const previewGuest = withPhone[0] || withoutPhone[0]
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div>
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.75rem', fontWeight: 700, color: '#6B5B52', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 0.5rem' }}>Message Template</p>
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.72rem', color: '#9B9B9B', margin: '0 0 0.5rem', lineHeight: 1.6 }}>
          Placeholders in <mark style={{ background: 'rgba(179,107,85,0.15)', color: '#B36B55', borderRadius: 3, padding: '0 3px' }}>brackets</mark> will be auto-filled for each guest.
        </p>
        <HighlightTextArea value={message} onChange={setMessage}/>
      </div>

      {previewGuest && (
        <div style={{ background: '#FAF5EC', border: '1px solid rgba(179,107,85,0.15)', borderRadius: '0.875rem', padding: '0.875rem 1rem' }}>
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.68rem', fontWeight: 700, color: '#9B9B9B', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 0.5rem' }}>Preview — {previewGuest.name}</p>
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.78rem', color: '#1A1A1A', margin: 0, lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>{personalize(message, previewGuest, wedding)}</p>
        </div>
      )}

      <div>
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.75rem', fontWeight: 700, color: '#6B5B52', margin: '0 0 0.5rem' }}>
          This reminder will be sent to <strong>{withPhone.length}</strong> guest{withPhone.length !== 1 ? 's' : ''}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', maxHeight: 180, overflowY: 'auto' }}>
          {withPhone.map(g => (
            <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0', borderBottom: '1px solid #F3F0EA' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#B36B55', flexShrink: 0 }}/>
              <span style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.78rem', color: '#1A1A1A', fontWeight: 600, flex: 1 }}>{g.name}</span>
              <span style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.72rem', color: '#9B9B9B' }}>{g.phone}</span>
            </div>
          ))}
        </div>
        {withoutPhone.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginTop: '0.75rem', background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.15)', borderRadius: '0.75rem', padding: '0.625rem 0.875rem' }}>
            <AlertTriangle size={14} color="#DC2626" style={{ flexShrink: 0, marginTop: 1 }}/>
            <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.75rem', color: '#DC2626', margin: 0, lineHeight: 1.6 }}>
              {withoutPhone.length} guest{withoutPhone.length !== 1 ? 's have' : ' has'} no phone number — {withoutPhone.map(g => g.name).join(', ')} — and will be skipped. Add a number on their guest card to include them.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Links phase ───────────────────────────────────────────────
function LinksPhase({ guests, withoutPhone, sentSet, onOpenWhatsApp, progress, isMobile }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ background: '#FAF5EC', borderRadius: '0.875rem', padding: '0.875rem 1rem' }}>
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.78rem', color: '#6B5B52', margin: 0, lineHeight: 1.7 }}>
          Tap <strong>Open WhatsApp</strong> for each guest. Their personalised reminder is pre-loaded — just press send, then come back for the next one.
        </p>
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
          <span style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.72rem', color: '#9B9B9B' }}>Progress: {sentSet.size} of {guests.length} sent</span>
          <span style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.72rem', color: '#B36B55', fontWeight: 700 }}>{progress}%</span>
        </div>
        <div style={{ height: 6, background: '#F3F0EA', borderRadius: 100, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(to right, #B36B55, #C4826A)', borderRadius: 100, transition: 'width 0.4s ease' }}/>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', overflowY: 'auto', maxHeight: isMobile ? '50vh' : 340 }}>
        {guests.map(g => {
          const sent = sentSet.has(g.id)
          return (
            <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', background: sent ? 'rgba(155,175,155,0.06)' : 'white', border: `1.5px solid ${sent ? 'rgba(155,175,155,0.3)' : '#E5E0D8'}`, borderRadius: '0.875rem', padding: '0.875rem 1rem', opacity: sent ? 0.75 : 1, transition: 'all 0.2s' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: sent ? 'rgba(155,175,155,0.15)' : '#F3F0EA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {sent ? <Check size={14} color="#5A7A5A"/> : <span style={{ fontFamily: 'Gibs, serif', fontSize: '0.85rem', color: '#BCBCBC' }}>{(g.name || '?')[0].toUpperCase()}</span>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontWeight: 700, fontSize: '0.85rem', color: sent ? '#7A917A' : '#1A1A1A', margin: '0 0 0.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.name}</p>
                <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.72rem', color: '#9B9B9B', margin: 0 }}>{g.phone}</p>
              </div>
              <button onClick={() => onOpenWhatsApp(g)} style={{
                fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.75rem', fontWeight: 700,
                color: sent ? '#7A917A' : 'white', background: sent ? 'rgba(155,175,155,0.12)' : '#25D366',
                border: 'none', borderRadius: 100, padding: isMobile ? '0.75rem 1rem' : '0.45rem 0.875rem',
                minHeight: isMobile ? 48 : 'auto', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                {sent ? '✓ Sent' : 'Open WhatsApp →'}
              </button>
            </div>
          )
        })}

        {withoutPhone.map(g => (
          <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: 'rgba(220,38,38,0.04)', border: '1px solid rgba(220,38,38,0.12)', borderRadius: '0.875rem' }}>
            <AlertTriangle size={14} color="#DC2626" style={{ flexShrink: 0 }}/>
            <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.75rem', color: '#DC2626', margin: 0 }}>
              <strong>{g.name}</strong> has no phone number — add it on their guest card to include them.
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
